/**
 * @fileoverview Utility functions for building common HTML elements.
 * Centralizes HTML generation patterns used across the codebase.
 * @module utils/HtmlBuilder
 */

import { escapeHtml } from './jrfuncs.js';

/**
 * Utility object for building common HTML elements.
 * Provides consistent patterns for the list items' buttons.
 */
export const HtmlBuilder = {

    /**
     * Creates an icon button with Bootstrap styling.
     *
     * @param {Object} options - Button configuration
     * @param {string} options.icon - Bootstrap icon class (e.g., 'bi-x-square')
     * @param {string} [options.title=''] - Tooltip title
     * @param {Object} [options.dataAttrs={}] - Data attributes as key-value pairs
     * @param {string} [options.classes=''] - Additional CSS classes
     * @param {string} [options.style=''] - Inline styles
     * @param {string} [options.btnClass='btn-sm'] - Button size class
     * @returns {string} HTML string for the button
     *
     * @example
     * HtmlBuilder.iconButton({
     *     icon: 'bi-trash',
     *     title: 'Delete item',
     *     dataAttrs: { uid: '123', action: 'delete' },
     *     classes: 'text-danger'
     * });
     */
    iconButton(options) {
        const {
            icon,
            title = '',
            dataAttrs = {},
            classes = '',
            style = '',
            btnClass = 'btn-sm'
        } = options;

        // attribute values (a uid comes from data or an import) are escaped; class/icon/style are code constants
        const dataStr = Object.entries(dataAttrs)
            .map(([key, value]) => `data-${key}="${escapeHtml(value)}"`)
            .join(' ');

        const styleAttr = style ? `style="${style}"` : '';

        return `<button class="btn ${btnClass} ${classes}" title="${escapeHtml(title)}" ${dataStr} ${styleAttr}>
            <i class="bi ${icon}"></i>
        </button>`;
    },


    /**
     * Creates a positioned delete button (absolute positioned in top-right corner).
     * Used for items in lists (bookmarks, history, etc.).
     *
     * @param {string} uid - The unique identifier for the item
     * @param {string} [title='Remove'] - Tooltip title
     * @param {string} [icon='bi-x-square'] - Bootstrap icon class
     * @returns {string} HTML string for the positioned delete button
     *
     * @example
     * HtmlBuilder.deleteButtonPositioned('item-123', 'Delete from history');
     */
    deleteButtonPositioned(uid, title = 'Remove', icon = 'bi-x-square') {
        return this.iconButton({
            icon,
            title,
            dataAttrs: { uid },
            classes: 'position-absolute top-0 end-0 p-1 item-delete-btn',
            style: 'z-index: 10;'
        });
    },


    /**
     * Creates a small inline action button (used in result items).
     *
     * @param {Object} options - Button configuration
     * @param {string} options.icon - Bootstrap icon class
     * @param {string} options.title - Tooltip title
     * @param {string} options.uid - Unique identifier for data attribute
     * @param {string} options.actionClass - CSS class for identifying the action
     * @returns {string} HTML string for the inline button
     *
     * @example
     * HtmlBuilder.inlineActionButton({
     *     icon: 'bi-play-circle-fill',
     *     title: 'Run this search',
     *     uid: 'search-123',
     *     actionClass: 'saved-search-execute-btn'
     * });
     */
    inlineActionButton(options) {
        const { icon, title, uid, actionClass } = options;
        return `<button class="btn btn-lnk p-0 ${actionClass} jrInlineTextButton"
                data-uid="${escapeHtml(uid)}"
                title="${escapeHtml(title)}">
            <i class="bi ${icon}"></i>
        </button>`;
    },

};
